import { useEffect, useState } from "react";
import { 
  Users, UserPlus, Trash2, Mail, Building2, IdCard, 
  Search, RefreshCw, X, Menu, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle2, MoreVertical
} from "lucide-react";
import api from "../api/axios";

export default function EmployeeList() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // Selection & Pagination State
  const [selectedIds, setSelectedIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // Form State
  const [form, setForm] = useState({ employeeId: "", full_name: "", email: "", department: "" });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Responsive Listener
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    fetchEmployees();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const res = await api.get("employees/");
      setEmployees(res.data);
    } catch (err) {
      console.error("Fetch error", err);
    } finally {
      setLoading(false);
    }
  };

  // --- VALIDATION & SUBMISSION ---
  const validate = () => {
    let errs = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.employeeId.trim()) errs.employeeId = "Required";
    if (!form.full_name.trim()) errs.full_name = "Required";
    if (!form.department.trim()) errs.department = "Required";
    if (!emailRegex.test(form.email)) errs.email = "Invalid email format";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await api.post("employees/", form);
      setForm({ employeeId: "", full_name: "", email: "", department: "" });
      setShowModal(false);
      fetchEmployees();
    } catch {
      alert("Error: Duplicate ID or Email.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const deleteEmployee = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      await api.delete(`employees/${id}/`);
      setSelectedIds(prev => prev.filter(i => i !== id));
      fetchEmployees();
    } catch { alert("Delete failed."); }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selectedIds.length} employees?`)) return;
    try {
      await Promise.all(selectedIds.map(id => api.delete(`employees/${id}/`)));
      setSelectedIds([]);
      fetchEmployees();
    } catch { alert("Bulk delete failed."); }
  };

  // --- LOGIC: SEARCH & PAGINATION ---
  const filtered = employees.filter(emp => 
    Object.values(emp).some(v => String(v).toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const toggleSelectAll = () => {
    if (selectedIds.length === paginatedData.length) setSelectedIds([]);
    else setSelectedIds(paginatedData.map(e => e.id));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  // --- STYLES (Dynamic & Responsive) ---
  const theme = {
    primary: '#2563eb',
    danger: '#ef4444',
    bg: '#f8fafc',
    card: '#ffffff',
    text: '#1e293b',
    border: '#e2e8f0'
  };

  return (
    <div style={{ backgroundColor: theme.bg, minHeight: '100vh', fontFamily: 'system-ui, sans-serif' }}>
      
      {/* HEADER / NAV */}
      <header style={{ 
        backgroundColor: '#fff', padding: '15px 25px', display: 'flex', 
        justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.border}`,
        position: 'sticky', top: 0, zIndex: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ background: theme.primary, padding: '8px', borderRadius: '8px' }}>
            <Users color="#fff" size={20} />
          </div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 700, margin: 0 }}>Employees</h2>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          style={{ 
            backgroundColor: theme.primary, color: '#fff', border: 'none', 
            padding: '8px 16px', borderRadius: '8px', fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}
        >
          <UserPlus size={18} />
          {!isMobile && "Add Employee"}
        </button>
      </header>

      <main style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '15px' : '30px' }}>
        
        {/* ACTION BAR */}
        <div style={{ 
          display: 'flex', flexDirection: isMobile ? 'column' : 'row', 
          gap: '15px', marginBottom: '25px', alignItems: isMobile ? 'stretch' : 'center' 
        }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={18} />
            <input 
              placeholder="Search employees..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ 
                width: '100%', padding: '12px 12px 12px 40px', borderRadius: '10px', 
                border: `1px solid ${theme.border}`, boxSizing: 'border-box', outlineColor: theme.primary
              }}
            />
          </div>

          {selectedIds.length > 0 && (
            <button 
              onClick={handleBulkDelete}
              style={{ 
                backgroundColor: '#fee2e2', color: theme.danger, border: `1px solid #fecaca`,
                padding: '10px 20px', borderRadius: '10px', fontWeight: 600, cursor: 'pointer'
              }}
            >
              Delete Selected ({selectedIds.length})
            </button>
          )}
        </div>

        {/* DATA DISPLAY: AUTO-ALIGNABLE */}
        <div style={{ backgroundColor: '#fff', borderRadius: '12px', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '50px', textAlign: 'center' }}><RefreshCw className="spin" /></div>
          ) : isMobile ? (
            /* MOBILE CARD VIEW */
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {paginatedData.map(emp => (
                <div key={emp.id} style={{ padding: '15px', borderBottom: `1px solid ${theme.border}`, display: 'flex', gap: '12px' }}>
                  <input 
                    type="checkbox" 
                    checked={selectedIds.includes(emp.id)} 
                    onChange={() => toggleSelectOne(emp.id)} 
                    style={{ width: '18px' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{emp.full_name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>{emp.employeeId} • {emp.department}</div>
                    <div style={{ fontSize: '0.85rem', color: theme.primary }}>{emp.email}</div>
                  </div>
                  <button onClick={() => deleteEmployee(emp.id)} style={{ background: 'none', border: 'none', color: theme.danger }}>
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            /* DESKTOP TABLE VIEW */
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', textAlign: 'left' }}>
                  <th style={tableHeaderStyle}>
                    <input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length === paginatedData.length && paginatedData.length > 0} />
                  </th>
                  <th style={tableHeaderStyle}>Employee</th>
                  <th style={tableHeaderStyle}>ID</th>
                  <th style={tableHeaderStyle}>Department</th>
                  <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map(emp => (
                  <tr key={emp.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                    <td style={tableCellStyle}>
                      <input type="checkbox" checked={selectedIds.includes(emp.id)} onChange={() => toggleSelectOne(emp.id)} />
                    </td>
                    <td style={tableCellStyle}>
                      <div style={{ fontWeight: 600 }}>{emp.full_name}</div>
                      <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{emp.email}</div>
                    </td>
                    <td style={tableCellStyle}><code>{emp.employeeId}</code></td>
                    <td style={tableCellStyle}>
                      <span style={{ background: '#eff6ff', color: theme.primary, padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 700 }}>
                        {emp.department}
                      </span>
                    </td>
                    <td style={{ ...tableCellStyle, textAlign: 'right' }}>
                      <button onClick={() => deleteEmployee(emp.id)} style={iconBtnStyle}><Trash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* PAGINATION FOOTER */}
          <div style={{ padding: '15px 25px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#fff' }}>
            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
              Page {currentPage} of {totalPages || 1}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={pagBtnStyle}>
                <ChevronLeft size={18} />
              </button>
              <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} style={pagBtnStyle}>
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </main>

      {/* ADD MODAL */}
      {showModal && (
        <div style={modalOverlayStyle}>
          <div style={{ ...modalContentStyle, width: isMobile ? '90%' : '450px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>New Employee</h3>
              <X onClick={() => setShowModal(false)} style={{ cursor: 'pointer' }} />
            </div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div>
                <label style={labelStyle}>Full Name</label>
                <input style={inputStyle(errors.full_name)} value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} placeholder="John Doe" />
                {errors.full_name && <small style={{color: theme.danger}}>{errors.full_name}</small>}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Employee ID</label>
                  <input style={inputStyle(errors.employeeId)} value={form.employeeId} onChange={e => setForm({...form, employeeId: e.target.value})} placeholder="EMP001" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Department</label>
                  <input style={inputStyle(errors.department)} value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="Sales" />
                </div>
              </div>
              <div>
                <label style={labelStyle}>Email Address</label>
                <input type="email" style={inputStyle(errors.email)} value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="john@company.com" />
                {errors.email && <small style={{color: theme.danger}}>{errors.email}</small>}
              </div>
              <button type="submit" disabled={isSubmitting} style={submitBtnStyle}>
                {isSubmitting ? "Processing..." : "Create Employee"}
              </button>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .spin { animation: spin 1s linear infinite; color: #2563eb; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        button:disabled { opacity: 0.4; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

// --- SHARED STYLES ---
const tableHeaderStyle = { padding: '15px 20px', fontSize: '0.8rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tableCellStyle = { padding: '15px 20px', fontSize: '0.9rem' };
const iconBtnStyle = { border: 'none', background: 'none', color: '#94a3b8', cursor: 'pointer' };
const pagBtnStyle = { 
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: '35px', height: '35px', 
  borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer' 
};
const modalOverlayStyle = { 
  position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', 
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(4px)'
};
const modalContentStyle = { backgroundColor: '#fff', padding: '30px', borderRadius: '15px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' };
const labelStyle = { display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '5px', color: '#475569' };
const inputStyle = (err) => ({ 
  width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${err ? '#ef4444' : '#cbd5e1'}`, 
  boxSizing: 'border-box', fontSize: '0.9rem' 
});
const submitBtnStyle = { 
  backgroundColor: '#2563eb', color: '#fff', border: 'none', padding: '12px', 
  borderRadius: '8px', fontWeight: 700, cursor: 'pointer', marginTop: '10px' 
};